import React, { useState } from 'react';
import {
  Box,
  Container,
  Paper,
  TextField,
  Button,
  Typography,
  Alert,
  CircularProgress,
  Stack,
  InputAdornment,
  FormHelperText,
} from '@mui/material';
import { Email, Phone, Message, Send } from '@mui/icons-material';
import { url } from '../constants';

const Contact = () => {
  const [formData, setFormData] = useState({
    message: '',
    email: '',
    phone: '',
  });

  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState('');
  const [apiError, setApiError] = useState('');

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors(prev => ({ ...prev, [name]: '' }));
    if (apiError) setApiError('');
  };

  const validate = () => {
    const newErrors = {};

    // Message validation
    if (!formData.message.trim() || formData.message.length < 10) {
      newErrors.message = 'Please enter at least 10 characters';
    }

    // At least one contact method required
    const emailValid = formData.email.trim() && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email.trim());
    const phoneValid = formData.phone.trim() && /^\d{10}$/.test(formData.phone.replace(/\D/g, ''));
    
    if (!emailValid && !phoneValid) {
      newErrors.email = 'Enter email OR phone number (at least one required)';
      newErrors.phone = 'Enter email OR phone number (at least one required)';
    } else {
      // Email validation if provided
      if (formData.email.trim() && !emailValid) {
        newErrors.email = 'Enter valid email address';
      }
      // Phone validation if provided  
      if (formData.phone.trim() && !phoneValid) {
        newErrors.phone = 'Enter valid 10-digit phone number';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    // ✅ FIXED: Build clean payload - NO null values!
    const email = formData.email.trim();
    const phoneCleaned = formData.phone.replace(/\D/g, '');
    
    const payload = {
      query: formData.message.trim(),
    };

    // ✅ Only add VALID fields
    if (email && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      payload.email = email;
    }
    if (phoneCleaned.length === 10) {
      payload.phone = phoneCleaned;
    }

    console.log('🚀 Sending payload:', payload); // DEBUG

    setLoading(true);
    setApiError('');
    setSuccess('');

    try {
      const response = await fetch(`${url}/users/queries`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.detail || 'Submission failed');
      }

      const result = await response.json();
      setSuccess(`✅ ${result.message}`);
      setFormData({ message: '', email: '', phone: '' });
    } catch (err) {
      setApiError(err.message || 'Something went wrong');
    } finally {
      setLoading(false);
    }
  };

  const hasContactMethod = formData.email.trim() || formData.phone.trim();

  return (
    <Box sx={{ minHeight: '700px', display: 'flex', alignItems: 'center', bgcolor: '#F8FAFC', py: 6 }}>
      <Container maxWidth="lg">
        <Paper elevation={6} sx={{ p: { xs: 4, md: 5 }, borderRadius: 3 }}>
          {/* Header */}
          <Box textAlign="center" mb={4}>
            <Typography variant="h4" fontWeight={700} color="#1a7f3a">
              Contact Us
            </Typography>
            <Typography color="text.secondary">
              We'd love to hear from you
            </Typography>
          </Box>

          {/* Alerts */}
          {success && <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess('')}>{success}</Alert>}
          {apiError && <Alert severity="error" sx={{ mb: 3 }} onClose={() => setApiError('')}>{apiError}</Alert>}

          {/* Form */}
          <Box component="form" onSubmit={handleSubmit}>
            <Stack spacing={3}>
              {/* Message */}
              <TextField
                label="Your Message *"
                name="message"
                multiline
                minRows={15}
                value={formData.message}
                onChange={handleChange}
                error={!!errors.message}
                helperText={errors.message}
                placeholder="Describe your issue or question..."
                fullWidth
                InputProps={{
                  startAdornment: <InputAdornment position="start"><Message /></InputAdornment>,
                }}
              />

              {/* Separate Email & Phone Fields */}
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 3 }}>
                <TextField
                  label="Email (optional)"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  error={!!errors.email}
                  helperText={errors.email}
                  placeholder="your@email.com"
                  fullWidth
                  InputProps={{
                    startAdornment: <InputAdornment position="start"><Email /></InputAdornment>,
                  }}
                />
                
                <TextField
                  label="Phone (optional)"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  error={!!errors.phone}
                  helperText={errors.phone || '10-digit number'}
                  placeholder="9876543210"
                  fullWidth
                  InputProps={{
                    startAdornment: <InputAdornment position="start"><Phone /></InputAdornment>,
                  }}
                />
              </Box>

              {/* Helper Text */}
              {!hasContactMethod && (
                <FormHelperText error sx={{ mt: -2 }}>
                  * At least one contact method (email OR phone) is required
                </FormHelperText>
              )}

              {/* Submit */}
              <Button
                type="submit"
                variant="contained"
                size="large"
                disabled={loading}
                startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <Send />}
                sx={{
                  bgcolor: '#1a7f3a',
                  py: 1.5,
                  fontWeight: 600,
                  '&:hover': { bgcolor: '#0f5328' },
                }}
              >
                {loading ? 'Sending...' : 'Send Message'}
              </Button>
            </Stack>
          </Box>
        </Paper>
      </Container>
    </Box>
  );
};

export default Contact;
